import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { formatINR, formatDate, formatDateTime, todayISO } from "../lib/format";
import {
    Coins, ArrowRight, CheckCircle2, Lock, Clock, Wallet, RefreshCw,
} from "lucide-react";

const STATUS_STYLES = {
    open:          "bg-slate-100 text-slate-700 border-slate-200",
    submitted:     "bg-amber-50 text-amber-800 border-amber-100",
    cash_received: "bg-blue-50 text-blue-800 border-blue-100",
    closed:        "bg-emerald-50 text-emerald-700 border-emerald-100",
};
const STATUS_LABEL = {
    open:          "Open",
    submitted:     "Submitted",
    cash_received: "Cash Received",
    closed:        "Closed",
};

export default function DayClose() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    return (
        <div className="space-y-5" data-testid="day-close-page">
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-brand-soft grid place-items-center">
                    <Coins className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-brand-royal font-semibold">
                        Daily Closing
                    </div>
                    <h2 className="font-display font-bold text-slate-900 text-xl">
                        Day Close &amp; Cash Reconciliation
                    </h2>
                </div>
            </div>

            {/* Self service — every user submits their own day */}
            <MyDayCard />

            {/* Admin queue */}
            {isAdmin && <AdminQueue />}
        </div>
    );
}

/* ---------- Employee self-service card ---------- */
function MyDayCard() {
    const { user } = useAuth();
    const [date, setDate] = useState(todayISO());
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actualCash, setActualCash] = useState("");
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/day-closings/summary", {
                params: { date, employee_id: user?.id },
            });
            setSummary(data);
            // Preload actual cash and reason from existing closing when available.
            if (data.closing) {
                if (data.closing.actual_cash_handed != null) {
                    setActualCash(String(data.closing.actual_cash_handed));
                }
                setReason(data.closing.variance_reason || "");
                setNotes(data.closing.notes || "");
            } else {
                setActualCash("");
                setReason("");
                setNotes("");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (user?.id) load();
    }, [date, user?.id]);

    const computed = summary?.computed || { cash_total: 0, online_total: 0, cash_count: 0, online_count: 0 };
    const closing = summary?.closing;
    const status = closing?.status || "open";
    const isLocked = status === "closed" || status === "cash_received";

    const actualNum = Number(actualCash) || 0;
    const variance = useMemo(
        () => +(actualNum - Number(computed.cash_total || 0)).toFixed(2),
        [actualNum, computed.cash_total],
    );
    const varianceLabel = variance > 0.01 ? "Excess" : variance < -0.01 ? "Shortage" : "Balanced";
    const varianceStyle = variance > 0.01
        ? "text-blue-700"
        : variance < -0.01
        ? "text-rose-700"
        : "text-emerald-700";

    async function submit() {
        if (actualCash === "") {
            toast.error("Enter the actual cash you handed over");
            return;
        }
        if (Math.abs(variance) > 0.01 && !reason.trim()) {
            toast.error("A reason is required for any shortage or excess");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/day-closings/submit", {
                date,
                actual_cash_handed: actualNum,
                variance_reason: reason.trim(),
                notes: notes.trim(),
            });
            toast.success("Day submitted for admin review");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Card className="rounded-2xl border-slate-100 p-5 sm:p-6" data-testid="day-close-my-card">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                        My Day
                    </div>
                    <h3 className="mt-1 font-display font-bold text-slate-900 text-lg">
                        Submit your day
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Only approved payments are included. Pending collections are excluded.
                    </p>
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                    <div>
                        <Label htmlFor="dc-date" className="text-[11px] font-semibold text-slate-600">Date</Label>
                        <Input
                            id="dc-date"
                            type="date"
                            value={date}
                            max={todayISO()}
                            onChange={(e) => setDate(e.target.value)}
                            className="mt-1.5 h-10 rounded-xl w-44"
                            data-testid="day-close-date"
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={load}
                        className="h-10 rounded-xl"
                        data-testid="day-close-refresh"
                    >
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
                    </Button>
                    <Badge className={`${STATUS_STYLES[status]} hover:opacity-100 h-7 px-2`}
                        data-testid={`day-close-status-${status}`}
                    >
                        {STATUS_LABEL[status]}
                    </Badge>
                </div>
            </div>

            {/* Totals */}
            <div className="mt-5 grid gap-3 sm:grid-cols-3 pt-5 border-t border-slate-100">
                <Tile
                    label={`Cash Collections (${computed.cash_count})`}
                    value={formatINR(computed.cash_total)}
                    icon={Coins}
                    testid="day-close-cash-total"
                />
                <Tile
                    label={`Online Collections (${computed.online_count})`}
                    value={formatINR(computed.online_total)}
                    icon={Wallet}
                    testid="day-close-online-total"
                />
                <Tile
                    label="Total Approved"
                    value={formatINR((computed.cash_total || 0) + (computed.online_total || 0))}
                    icon={CheckCircle2}
                    accent
                    testid="day-close-grand-total"
                />
            </div>

            {/* Submit block */}
            <div className="mt-5 pt-5 border-t border-slate-100 grid gap-4 md:grid-cols-2">
                <div>
                    <Label htmlFor="dc-cash" className="text-xs font-semibold text-slate-600">
                        Actual cash handed over (₹)
                    </Label>
                    <Input
                        id="dc-cash"
                        type="number"
                        min={0}
                        step="0.01"
                        value={actualCash}
                        onChange={(e) => setActualCash(e.target.value)}
                        disabled={isLocked || loading}
                        placeholder={`Expected ${formatINR(computed.cash_total || 0)}`}
                        className="mt-1.5 h-11 rounded-xl num"
                        data-testid="day-close-actual"
                    />
                    <div className={`mt-2 text-xs font-semibold ${varianceStyle}`}
                        data-testid="day-close-variance">
                        {actualCash === ""
                            ? <span className="text-slate-400">Enter to see variance</span>
                            : `${varianceLabel}: ${formatINR(Math.abs(variance))}${variance === 0 ? "" : (variance > 0 ? " over expected" : " short of expected")}`}
                    </div>
                </div>
                <div>
                    <Label htmlFor="dc-reason" className="text-xs font-semibold text-slate-600">
                        Reason for shortage / excess
                    </Label>
                    <Textarea
                        id="dc-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={isLocked || loading || Math.abs(variance) <= 0.01}
                        placeholder={Math.abs(variance) <= 0.01 ? "Not required — cash matches" : "Explain the variance"}
                        rows={3}
                        className="mt-1.5 rounded-xl"
                        maxLength={500}
                        data-testid="day-close-reason"
                    />
                </div>
                <div className="md:col-span-2">
                    <Label htmlFor="dc-notes" className="text-xs font-semibold text-slate-600">
                        Notes (optional)
                    </Label>
                    <Textarea
                        id="dc-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={isLocked || loading}
                        rows={2}
                        maxLength={1000}
                        className="mt-1.5 rounded-xl"
                        data-testid="day-close-notes"
                    />
                </div>
                <div className="md:col-span-2 flex items-center justify-between pt-3 border-t border-slate-100">
                    <div className="text-[11px] text-slate-500">
                        {status === "closed" && (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                                <Lock className="h-3.5 w-3.5" /> This day is closed and locked.
                            </span>
                        )}
                        {status === "cash_received" && (
                            <span className="inline-flex items-center gap-1 text-blue-700 font-semibold">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Cash confirmed by admin — awaiting close.
                            </span>
                        )}
                        {status === "submitted" && (
                            <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                                <Clock className="h-3.5 w-3.5" /> Submitted — waiting for admin to confirm cash.
                            </span>
                        )}
                        {status === "open" && (
                            <span className="text-slate-500">
                                {(computed.cash_count + computed.online_count) === 0
                                    ? "No approved transactions yet for this day."
                                    : "Ready to submit your day."}
                            </span>
                        )}
                    </div>
                    <Button
                        onClick={submit}
                        disabled={isLocked || submitting}
                        className="h-11 rounded-xl bg-brand-navy hover:bg-brand-royal"
                        data-testid="day-close-submit"
                    >
                        {submitting ? "Submitting…" : (status === "submitted" ? "Resubmit" : "Submit day")}
                        <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                </div>
            </div>

            {/* Timeline */}
            {closing && (
                <div className="mt-5 pt-5 border-t border-slate-100 grid gap-2 text-xs text-slate-600 sm:grid-cols-3">
                    {closing.submitted_at && (
                        <div>Submitted <span className="font-semibold">{formatDateTime(closing.submitted_at)}</span></div>
                    )}
                    {closing.confirmed_at && (
                        <div>Cash confirmed <span className="font-semibold">{formatDateTime(closing.confirmed_at)}</span> by {closing.confirmed_by}</div>
                    )}
                    {closing.closed_at && (
                        <div>Closed <span className="font-semibold">{formatDateTime(closing.closed_at)}</span> by {closing.closed_by}</div>
                    )}
                </div>
            )}
        </Card>
    );
}

/* ---------- Admin queue ---------- */
function AdminQueue() {
    const [tab, setTab] = useState("submitted");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState("");

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/day-closings", { params: { status: tab } });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, [tab]);

    async function act(kind, r) {
        const label = kind === "confirm-cash" ? "Confirm Cash" : "Close Day";
        if (!window.confirm(`${label} for ${r.employee_name || r.employee_username} · ${formatDate(r.date)}?`)) return;
        setBusy(r.id + kind);
        try {
            await api.post(`/day-closings/${r.id}/${kind}`);
            toast.success(`${label} done`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy("");
        }
    }

    const tabs = [
        { key: "submitted",     label: "Submitted",     icon: Clock },
        { key: "cash_received", label: "Cash Received", icon: CheckCircle2 },
        { key: "closed",        label: "Closed",        icon: Lock },
    ];

    return (
        <Card className="rounded-2xl border-slate-100 p-4 sm:p-5" data-testid="day-close-admin-queue">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-brand-royal font-semibold">
                        Admin Queue
                    </div>
                    <h3 className="mt-0.5 font-display font-bold text-slate-900 text-lg">
                        Reconciliation queue
                    </h3>
                </div>
                <Button variant="outline" size="sm" onClick={load} className="h-9 rounded-lg">
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
                </Button>
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="day-close-admin-tabs">
                {tabs.map((t) => {
                    const active = tab === t.key;
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setTab(t.key)}
                            className={`h-9 rounded-full px-3 text-xs font-semibold border transition inline-flex items-center gap-1.5 ${
                                active
                                    ? "bg-brand-navy text-white border-brand-navy"
                                    : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                            }`}
                            data-testid={`day-close-admin-tab-${t.key}`}
                        >
                            <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="mt-4">
                {loading ? (
                    <div className="p-8 text-center text-sm text-slate-400">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400"
                        data-testid="day-close-admin-empty">
                        No {STATUS_LABEL[tab].toLowerCase()} day closings.
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 rounded-xl border border-slate-100 overflow-hidden">
                        {rows.map((r) => (
                            <div key={r.id}
                                className="p-3 sm:p-4 flex flex-wrap items-start justify-between gap-3"
                                data-testid={`day-close-row-${r.id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="font-semibold text-slate-900 truncate">
                                            {r.employee_name || r.employee_username}
                                        </div>
                                        <Badge className={`${STATUS_STYLES[r.status]} hover:opacity-100`}>
                                            {STATUS_LABEL[r.status]}
                                        </Badge>
                                        <span className="text-[11px] text-slate-500 font-mono">
                                            {formatDate(r.date)}
                                        </span>
                                    </div>
                                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                        <Mini label="Cash Total" value={formatINR(r.cash_total)} />
                                        <Mini label="Online Total" value={formatINR(r.online_total)} />
                                        <Mini label="Cash Handed" value={formatINR(r.actual_cash_handed)} />
                                        <Mini
                                            label="Variance"
                                            value={formatINR(Math.abs(Number(r.variance || 0)))}
                                            tone={Number(r.variance || 0) < -0.01 ? "rose" : Number(r.variance || 0) > 0.01 ? "blue" : "emerald"}
                                        />
                                    </div>
                                    {r.variance_reason && (
                                        <div className="mt-2 text-[11px] text-slate-600 italic">
                                            Reason: <span className="text-slate-800 not-italic">{r.variance_reason}</span>
                                        </div>
                                    )}
                                    <div className="mt-1.5 text-[11px] text-slate-500">
                                        {r.submitted_at && <>Submitted {formatDateTime(r.submitted_at)}</>}
                                        {r.confirmed_at && <> · Cash confirmed {formatDateTime(r.confirmed_at)}</>}
                                        {r.closed_at && <> · Closed {formatDateTime(r.closed_at)}</>}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5 shrink-0">
                                    {r.status === "submitted" && (
                                        <Button
                                            size="sm"
                                            onClick={() => act("confirm-cash", r)}
                                            disabled={busy === r.id + "confirm-cash"}
                                            className="h-9 rounded-lg bg-blue-900 hover:bg-blue-800"
                                            data-testid={`day-close-confirm-${r.id}`}
                                        >
                                            Confirm Cash
                                        </Button>
                                    )}
                                    {r.status === "cash_received" && (
                                        <Button
                                            size="sm"
                                            onClick={() => act("close", r)}
                                            disabled={busy === r.id + "close"}
                                            className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700"
                                            data-testid={`day-close-close-${r.id}`}
                                        >
                                            Close Day
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
}

function Tile({ label, value, icon: Icon, accent, testid }) {
    return (
        <div
            className={`rounded-xl border p-3 flex items-center gap-3 ${
                accent
                    ? "border-brand-royal/25 bg-brand-soft/40"
                    : "border-slate-100 bg-white"
            }`}
            data-testid={testid}
        >
            <div className="h-9 w-9 rounded-lg bg-white border border-slate-100 grid place-items-center shrink-0">
                {Icon ? <Icon className="h-4 w-4 text-brand-royal" strokeWidth={1.75} /> : null}
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate">
                    {label}
                </div>
                <div className={`num mt-0.5 text-base font-bold ${accent ? "text-brand-royal" : "text-slate-900"}`}>
                    {value}
                </div>
            </div>
        </div>
    );
}

function Mini({ label, value, tone }) {
    const toneClass =
        tone === "rose"    ? "text-rose-700" :
        tone === "blue"    ? "text-blue-700" :
        tone === "emerald" ? "text-emerald-700" :
                             "text-slate-900";
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {label}
            </div>
            <div className={`num mt-0.5 text-sm font-bold ${toneClass}`}>{value}</div>
        </div>
    );
}
